import {TimelineLite, TimelineMax} from 'gsap';
import {IFinalEvaluatedArea, IMentalGameData} from "../../server/MentalData";
import {Mental} from "../../Mental";
import {MentalHelper} from "../../utils/MentalHelper";
import {XFeaturePresentation} from "../xFeature/XFeaturePresentation";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {MentalEnhancerXWaysAnim} from "../../features/MentalEnhancerXWaysAnim";
import {ReelEnhancer} from "../../server/ReelEnhancerData";
import {FeaturePresentationHelper} from "../../utils/FeaturePresentationHelper";
import {SoundManager} from "../../sound/SoundManager";
import {MentalSoundConfig} from "../../MentalSoundConfig";
import {MentalScatter2Explosion} from "../../features/MentalScatter2Explosion";
import {MentalFeatureIndicatorView} from "../../features/MentalFeatureIndicatorView";

enum EnhancerReelSymbols {
    XWAYS = "X_WAYS"
}

interface IEnhancerKnifeYPosHeight {
    yPos: number,
    height: number
}

export class EnhancerReelPresentation extends PIXI.Container {
    private _tl: TimelineMax;
    // x_ways opening animation
    private _xWaysAnim1: MentalEnhancerXWaysAnim;
    private _xWaysAnim3: MentalEnhancerXWaysAnim;

    constructor() {
        super();
        this.init();
    }

    // #305 => all reel ids permissible except reel id at which 2 S2s becomes in same row.
    public static handleDeadMultiData(gameData: IMentalGameData, reelId: number, splitReel: number, symId: number, splitIdx: number): void {
        let deadMultiplierPlacedPositions: boolean[][][] = gameData.deadMultiplierPlacedPositions,
            deadMultiplierPlacedValue: number[][][] = gameData.deadMultiplierPlacedValue,
            finalEvaluatedArea: IFinalEvaluatedArea[][][] = gameData.finalEvaluatedArea,
            currentSymbolSize: number[][] = gameData.currentSymbolSize,
            multiplierOccurrence: number,
            currentSize: number;

        // we needn't handle cases when split is happening at non 0 index, if multiplier is already not placed on that symbol.
        if (splitIdx === 0 && reelId !== splitReel && deadMultiplierPlacedPositions[reelId][symId][0]) {
            multiplierOccurrence = finalEvaluatedArea[reelId][symId][0].multiplierOccurrence;
            currentSize = currentSymbolSize[reelId][symId];
            // whenever we have the multiplier occurrence for that symbol strictly less than ongoing currentSize of that symbol, we insert a false and 1 at 1st position of that symbol and take out the last element out of them.
            // example for deadMultiplierPlacedPositions, current => result: - true, true => true, false, true;
            // another example, if 2 S2s in same row, having xSplit at 0 index then at any other reel if, (again explaining current => result) true, false, true => true, false, false, true
            if (multiplierOccurrence < currentSize) {
                deadMultiplierPlacedPositions[reelId][symId].splice(1, 0, false);
                deadMultiplierPlacedValue[reelId][symId].splice(1, 0, 1);

                deadMultiplierPlacedValue[reelId][symId].pop();
                deadMultiplierPlacedPositions[reelId][symId].pop();
            }
        }
    }

    private static playKnifeAnimation(positions: IEnhancerKnifeYPosHeight[]): void {
        const numReels: number = 5;
        for (let i: number = 0; i < numReels; i++) {
            Mental.xSplitAnimations.playKnifeAnimation(i, positions[i].yPos);
        }
    }

    public playEnhancerXSplitPresentation(): TimelineMax {
        this._tl = MentalHelper.timeLineKiller(this._tl, new TimelineMax());
        this.playEnhancerXSplit();
        return this._tl;
    }

    /**
     * showing enhancer xWays logo and bg
     */
    public showXWaysLogoBg(data: IMentalGameData): TimelineMax {
        this._tl = MentalHelper.timeLineKiller(this._tl, new TimelineMax());
        for (let i: number = 0; i < 2; i++) {
            if (data.specialReelsSymbols[i] === EnhancerReelSymbols.XWAYS) {
                if (i === 0) {
                    this._tl.add(this._xWaysAnim1.playOpeningAnimationEnhancers(), 0);
                    // #291, we want the above time to be calculated
                    this._tl.add(this._xWaysAnim1.playExpandAnimationEnhancers());
                } else {
                    this._tl.add(this._xWaysAnim3.playOpeningAnimationEnhancers(), 0);
                }
            }
        }

        return this._tl;
    }

    /**
     * expanding enhancer xWays => #291 => this is used only for E2, E1 has been already taken care while opening.
     */
    public expandXWaysPresentation(): TimelineMax {
        this._tl = MentalHelper.timeLineKiller(this._tl, new TimelineMax());

        this._tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]));

        this._tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(3, 2, 0, false, 634.5, 99));

        this._tl.add(this._xWaysAnim3.playExpandAnimationEnhancers());

        return this._tl;
    }

    public hideAllXWaysOverlays(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            gameData: IMentalGameData = Mental.model.gameData;

        for (let i = 0; i < gameData.specialReelsOpen.length; i++) {
            if (gameData.specialReelsOpen[i] && gameData.specialReelsSymbols[i] === EnhancerReelSymbols.XWAYS) {
                const reelId: number = i === 0 ? 1 : 3;
                Mental.xWaysCounter.updateXwaysCounterFromEnhancerXways(reelId);
            }
        }

        tl.add(this._xWaysAnim1.hideXWaysOverlayAnim(), 0);
        tl.add(this._xWaysAnim3.hideXWaysOverlayAnim(), 0);
        return tl;
    }

    public doTint(tint: number): void {
        this._xWaysAnim1.doTint(tint);
        this._xWaysAnim3.doTint(tint);
    }

    private init(): void {
        const mask1: PIXI.Container = Mental.reelArea.getReel(1).layerSet.layers[1].mask as PIXI.Container;

        this._xWaysAnim1 = new MentalEnhancerXWaysAnim(1, () => {
        }, 2);
        this._xWaysAnim1.mask = mask1;

        const mask3: PIXI.Container = Mental.reelArea.getReel(3).layerSet.layers[1].mask as PIXI.Container;
        this._xWaysAnim3 = new MentalEnhancerXWaysAnim(3, () => {
        }, 2);
        this._xWaysAnim3.mask = mask3;

        this.addChild(this._xWaysAnim1, this._xWaysAnim3);
        this.position.set(72, 684); //144/2, 90+99+198+198
    }

    /**
     * playing split for enhancer symbols
     * reading final symbols size
     */
    private playEnhancerXSplit(): void {
        const gameData: IMentalGameData = Mental.model.gameData,
            duration: number = (Mental.timeSegment * 3),
            enhancerReel: number = (gameData.specialReelsSymbols.indexOf(ReelEnhancer.XSPLIT) === 0 ? 1 : 3);

        let timeForScatterExplosion: number = Mental.timeSegment,
            positions: IEnhancerKnifeYPosHeight[] = [];

        this._tl.add(() => {positions = this.getEnhancerKnifePositions(gameData)}, 0);

        if (enhancerReel === 3) {
            this._tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]), 0);

            this._tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(enhancerReel, 2, 0, false, 634.5, 99), 0);

            timeForScatterExplosion = 0;
        }

        this._tl.add(() => {
            EnhancerReelPresentation.playKnifeAnimation(positions);

            // #92
            MentalFeatureIndicatorView.playXSplitActiveIndicator();

            // #203, #332
            SoundManager.playSoundEffect(MentalSoundConfig.X_SPLIT, 0, 1);
        }, Mental.timeSegment - timeForScatterExplosion);

        // #86
        if (gameData.betWayWins.length > 0) {
            this._tl.add(() => {
                for (let i: number = 0; i < 5; i++) {
                    MentalScatter2Explosion.playScatter2ExplosionAnimation(i, 2, 0, false, positions[i].yPos, positions[i].height);
                }
            }, (Mental.timeSegment * 2) - timeForScatterExplosion);

            this._tl.add(() => this.doSplitActionOnSymbols(gameData, enhancerReel), ((Mental.timeSegment * 2) - timeForScatterExplosion) + 0.29);

            this._tl.add(() => {
            }, duration - timeForScatterExplosion);
        } else {
            this._tl.add(() => {
                this.doSplitActionOnSymbols(gameData, enhancerReel);

                // #332
                SoundManager.stopXSplit();
            }, ((Mental.timeSegment * 2) - timeForScatterExplosion));
        }

        // #353 and to fix => Enhancer xSplit and reels not getting multiplier after xSplit.
        const time: number = this._tl.totalDuration(),
            numReels = 5;

        for (let i: number = 0; i < numReels; i++) {
            if (i === 1 && gameData.specialReelsSymbols[0] !== ReelEnhancer.XWAYS && gameData.specialReelsSymbols[0] !== ReelEnhancer.XSPLIT) {
                this._tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(gameData.stickyDeadSymCounter, 1, 2, 1), time);
            } else if (i === 3) {
                if (gameData.specialReelsOpen[1]) {
                    if (gameData.specialReelsSymbols[1] !== ReelEnhancer.XWAYS && gameData.specialReelsSymbols[1] !== ReelEnhancer.XSPLIT) {
                        this._tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(gameData.stickyDeadSymCounter, 3, 2, 1), time);
                    }
                } else {
                    const innerSymId: number = gameData.deadMultiplierPlacedPositions[i][1].indexOf(false);
                    this._tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(gameData.stickyDeadSymCounter, 3, 1, innerSymId), time);
                }
            } else {
                const innerSymId: number = gameData.deadMultiplierPlacedPositions[i][2].indexOf(false);
                this._tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(gameData.stickyDeadSymCounter, i, 2, innerSymId), time);
            }
        }
    }

    private getEnhancerKnifePositions(gameData: IMentalGameData): IEnhancerKnifeYPosHeight[] {
        const symHeight: number = 198,
            reelAreaTop: number = 90,
            numReels: number = 5,
            positions: IEnhancerKnifeYPosHeight[] = [];

        for (let i: number = 0; i < numReels; i++) {
            const j: number = (i === 3 ? (gameData.specialReelsOpen[1] ? 2 : 1) : 2),
                currentSymbolSize: number[][] = gameData.currentSymbolSize, // shallow copy
                extraY: number = (i % 2 === 1 ? (symHeight / 2) : 0) + reelAreaTop,
                currSymHeight: number = (symHeight / currentSymbolSize[i][j]);
            let yPos: number = extraY,
                height: number = 0,
                isEnhancerReel: boolean = ((i === 1) || (i === 3));

            if (isEnhancerReel) {
                // don't change the following yPos.
                if (i === 1 && gameData.specialReelsSymbols[0] === ReelEnhancer.XWAYS) {
                    yPos = this._xWaysAnim1.getBGCenterYPos();

                    height = this._xWaysAnim1.getBGHeight();

                    // if other enhancer has xWays on it
                    isEnhancerReel = false;
                } else if (i === 3 && gameData.specialReelsSymbols[1] === ReelEnhancer.XWAYS) {
                    yPos = this._xWaysAnim3.getBGCenterYPos();

                    height = this._xWaysAnim3.getBGHeight();

                    // if other enhancer is closed or has xWays on it
                    isEnhancerReel = false;
                }
            } else {
                yPos += (symHeight * j) + (currSymHeight / 2);
            }

            // if other enhancer(E2) is closed.
            if (i === 3 && j === 1) {
                isEnhancerReel = false;

                // if 4th reel has xWays played on it.
                if (gameData.xWaysPlayed[1]) {
                    // S2 was at symId 1 and had xWays on it's 0 index.
                    if (gameData.scatter2Position[3] === 1 && gameData.secondS2FeatureTriggered[0] === ReelEnhancer.XWAYS) {
                        yPos = Mental.s2xWaysFeature.getXWaysAnimBgYPos(3, 0, true);

                        height = Mental.s2xWaysFeature.getXWaysAnimBgHeight(3, 0);
                    }
                    // otherwise we use per sym height due to xWays played on that reel and use the absolute index with the help of current symbol size.
                    else {
                        const heightPerSym: number = FeaturePresentationHelper._symbolHeightOnReel[3];

                        yPos += (heightPerSym * (currentSymbolSize[i][0])) + (heightPerSym / 2);

                        height = heightPerSym;
                    }
                } else {
                    yPos += (symHeight * j) + (currSymHeight / 2);

                    height = currSymHeight;
                }
            }

            if (isEnhancerReel) {
                yPos = 634.5; // this is the y-position for enhancer xSplit.

                height = 99;
            }

            positions.push({
                yPos: yPos,
                height: height
            })
        }

        return positions;
    }

    private doSplitActionOnSymbols(gameData: IMentalGameData, enhancerReel: number): void {
        Mental.xSplitAnimations.stopAllAnimationsEnhancer();

        const numReels: number = 5;

        for (let i: number = 0; i < numReels; i++) {
            const j: number = (i === 3 ? (gameData.specialReelsOpen[1] ? 2 : 1) : 2),
                currentSymbolSize: number[][] = gameData.currentSymbolSize; // shallow copy

            if (!currentSymbolSize[i][j]) {
                Logger.warn("feature triggered on closed enhancer, playEnhancerXSplit");
            }

            let symbolNames: string[] = [Mental.model.data.evaluatedArea[i][j]],
                canSkipWildAnim: boolean = false;

            if (symbolNames.indexOf("S2") !== -1) { // to fix #163
                symbolNames = (i === 1) ? gameData.firstS2SymbolName : gameData.secondS2SymbolName;

                const featureTriggered = (i === 1) ? gameData.firstS2FeatureTriggered : gameData.secondS2FeatureTriggered;
                const symOnFirstIndex: string = symbolNames[0]; // enhancer xSplit will cut top symbol

                if (featureTriggered[0] === ReelEnhancer.XWAYS) { // only top feature matters
                    for (let k: number = 0; k < 3; k++) {
                        symbolNames.unshift(symOnFirstIndex);
                    }
                } else {
                    symbolNames.unshift(symOnFirstIndex);
                }
                canSkipWildAnim = (symbolNames.includes("W"));
            } else if (i !== enhancerReel && symbolNames.includes("W")) {
                canSkipWildAnim = true;
            }

            let isSplitActionOnXWays: boolean = false;

            if (i === 1) {
                // if xSplit on E2, then check xWays on E1.
                if (enhancerReel === 3 && gameData.xWaysPlayed[2] && (gameData.specialReelsSymbols[0] === ReelEnhancer.XWAYS)) { // modified for #421 -> making sure E2 has xWays on it
                    isSplitActionOnXWays = true;
                }
            } else if (i === 3) {
                // if xSplit on E1, then check xWays for S2 if E2 is closed otherwise E2 will be open and check xWays on E2.
                if (enhancerReel === 1 &&
                    ((j === 1 && gameData.secondS2FeatureTriggered[0] === ReelEnhancer.XWAYS && gameData.xWaysPlayed[1] && gameData.scatter2Position[i] === j) // modified for #421 -> split from Enhancer will always act upon top index of S2
                        || (gameData.specialReelsSymbols[1] === ReelEnhancer.XWAYS))) {
                    isSplitActionOnXWays = true;
                }
            }

            currentSymbolSize[i][j] += (isSplitActionOnXWays ? 3 : 1);

            // #305
            EnhancerReelPresentation.handleDeadMultiData(gameData, i, enhancerReel, j, 0);

            XFeaturePresentation.playXSplitForEnhancer(i, j, symbolNames, canSkipWildAnim);

            if (i === 3 && isSplitActionOnXWays && gameData.secondS2FeatureTriggered[0] === ReelEnhancer.XWAYS && !gameData.specialReelsOpen[1]) { // to fix #284
                Mental.s2xWaysFeature.resizeXWaysOverlayWhenE2isClosed(gameData.secondS2FeatureTriggered.length, i, new TimelineLite(), false, true, 0);
            }
            if (i === 3 && (gameData.xWaysPlayed[1] || gameData.xWaysPlayed[3])) {
                FeaturePresentationHelper.resizeSymbolWithInReel(3, gameData.xWaysPlayed[3]);
                if (!gameData.specialReelsOpen[1]) { // to fix #337
                    Mental.s2xWaysFeature.resizeXWaysOverlay(gameData.secondS2FeatureTriggered.length, i, new TimelineLite());
                }
            }
            if (i === 1 && (gameData.xWaysPlayed[0] || gameData.xWaysPlayed[2])) {
                FeaturePresentationHelper.resizeSymbolWithInReel(1, gameData.xWaysPlayed[2]);
            }

            if (i === 1 || i === 3) {
                const index: number = ((i === 1) ? 0 : 1);
                let isXWaysInEnhancer: boolean = (gameData.specialReelsSymbols[index] === ReelEnhancer.XWAYS);
                if (isXWaysInEnhancer) {
                    if (i === 1) {
                        this._xWaysAnim1.resizeEnhancerXWaysBg();
                        Mental.s2xWaysFeature.resizeXWaysOverlay(gameData.firstS2FeatureTriggered.length, i, new TimelineLite(), true);
                    } else {
                        this._xWaysAnim3.resizeEnhancerXWaysBg();
                        Mental.s2xWaysFeature.resizeXWaysOverlay(gameData.secondS2FeatureTriggered.length, i, new TimelineLite(), true);
                    }
                    Mental.enhancerFrame.resizeEnhancerFrameOnXWayXSplit(i);
                }
            }

            // #116
            SoundManager.playSoundEffect(MentalSoundConfig.REEL_SPLIT[i]);
        }
    }
}